import { IDocDocumentation, IDocNode, loadExtendedDoc } from './i-doc.documentation';

export type HintLookup = (id: string) => string;

/**
 * Будує один Markdown-файл з дерева документації для обраної ролі: заголовок
 * на кожен вузол (сторінка/вкладка/секція/модалка) з i-doc id, коротким hint
 * (за наявності) і повним розширеним описом з `common/i-doc/docs/*.doc.md`
 * (за наявності). Те саме дерево, що показує /documentation — просто в
 * одному файлі, який можна віддати поза застосунком.
 */
export async function buildMarkdownExport(
  tree: IDocDocumentation,
  translateLabel: (labelKey: string) => string,
  lookupHint: HintLookup,
  roleLabel: string,
): Promise<string> {
  const lines: string[] = [`# Документація Everyday Studio — ${roleLabel}`, ''];

  const renderNode = async (node: IDocNode, depth: number): Promise<void> => {
    const heading = '#'.repeat(Math.min(depth + 2, 6));
    const label = depth === 0 ? translateLabel(node.label) : node.label;
    lines.push(`${heading} ${label} \`${node.id}\``);
    lines.push('');

    const hint = lookupHint(node.id);
    if (hint) {
      lines.push(`> ${hint}`);
      lines.push('');
    }

    const extended = await loadExtendedDoc(node.id);
    if (extended) {
      lines.push(extended.trim());
      lines.push('');
    }

    if (node.children) {
      for (const child of node.children) {
        await renderNode(child, depth + 1);
      }
    }
  };

  for (const group of tree.groups) {
    for (const node of group.nodes) {
      await renderNode(node, 0);
    }
  }

  return lines.join('\n');
}

export function downloadMarkdown(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
